import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
  Button,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { HiDocumentRemove } from "react-icons/hi";
import FormStatusAlert from "../components/FormStatusAlert";

const UploadedFormsTable = ({
  forms,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onDownload,
  onEdit,
  onOpenSettings,
  onDelete,
  deleteLoading,
  onSort,
  sortField,
  sortDirection,
  downloadLoading,
  fetchForms,
  onSync,
  restricted = false,
}) => {
  const columnCount = restricted ? 5 : 6;
  const [openStatusAlert, setOpenStatusAlert] = useState(false);
  const [selectedFormForStatus, setSelectedFormForStatus] = useState(null);

  const handleCloseStatusAlert = () => {
    setOpenStatusAlert(false);
    setSelectedFormForStatus(null);
  };

  // Sortable header component
  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    const isAsc = isActive && sortDirection === "asc";
    const isDesc = isActive && sortDirection === "desc";

    return (
      <TableCell
        sx={{
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => onSort(field)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {children}
          {isActive && (
            <>
              {isAsc && <ArrowUpwardIcon fontSize="small" />}
              {isDesc && <ArrowDownwardIcon fontSize="small" />}
            </>
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <SortableHeader field="form_name">Form Name</SortableHeader>
                <SortableHeader field="county">County</SortableHeader>
                <SortableHeader field="municipality">
                  Municipality
                </SortableHeader>
                <TableCell align="center">Retired</TableCell>
                {!restricted && (
                  <TableCell align="center">Production Sync</TableCell>
                )}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : forms.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columnCount}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No forms found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                forms.map((form) => (
                  <TableRow key={form.id} hover>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {form.label || form.fileName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {form.county ? (
                        <Chip
                          label={form.county}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {form.municipality ? (
                        <Chip
                          label={form.municipality}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          color: form.retired ? "error.main" : "text.secondary",
                          fontWeight: form.retired ? 700 : 400,
                        }}
                      >
                        {form.retired ? "YES" : "NOT"}
                      </Typography>
                    </TableCell>
                    {!restricted && (
                      <TableCell align="center">
                        {form.status !== "active" ? (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        ) : form.production_sync?.synced ? (
                          <Typography
                            variant="body2"
                            sx={{ color: "success.main", fontWeight: 700 }}
                          >
                            SYNCED
                          </Typography>
                        ) : (
                          <Button
                            size="small"
                            color="error"
                            variant="text"
                            onClick={() => onSync(form)}
                            sx={{ fontWeight: 700 }}
                          >
                            UNSYNCED
                          </Button>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {restricted && form.production_sync?.synced ? (
                          <Tooltip title="Synced production forms can only be edited by an administrator">
                            <span>
                              <IconButton size="small" disabled>
                                <SettingsIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : form.status === "active" ? (
                          <Tooltip title="Form Settings">
                            <IconButton
                              size="small"
                              onClick={() => onOpenSettings(form)}
                              color="primary"
                            >
                              <SettingsIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Edit Form">
                            <IconButton
                              size="small"
                              onClick={() =>
                                onEdit(
                                  form.form_id,
                                  form.label || form.fileName,
                                  form.county,
                                  form?.jurisdictions_id,
                                  form.status,
                                  form.doc,
                                  form.signatures,
                                )
                              }
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Download Template">
                          <IconButton
                            size="small"
                            onClick={() => onDownload(form.form_id)}
                            color="primary"
                            className={
                              downloadLoading === form.id ? "rotate" : ""
                            }
                          >
                            {downloadLoading === form.id ? (
                              <RefreshIcon />
                            ) : (
                              <DownloadIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        {form.canDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() =>
                                onDelete(
                                  form.form_id,
                                  form.label || form.fileName,
                                )
                              }
                              color="error"
                              className={
                                deleteLoading === form.id ? "rotate" : ""
                              }
                            >
                              {deleteLoading === form.id ? (
                                <RefreshIcon />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {form.status === "active" &&
                          (!restricted || !form.production_sync?.synced) && (
                            <Tooltip title="Inactivate Form">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedFormForStatus(form);
                                  setOpenStatusAlert(true);
                                }}
                                color="error"
                              >
                                <HiDocumentRemove size={22} />
                              </IconButton>
                            </Tooltip>
                          )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>
      <FormStatusAlert
        status={"inactive"}
        open={openStatusAlert}
        handleClose={handleCloseStatusAlert}
        formId={selectedFormForStatus?.form_id}
        fetchData={fetchForms}
        requireSyncPassword={
          selectedFormForStatus?.production_sync?.synced === true
        }
      />
    </>
  );
};

export default UploadedFormsTable;
